import { LogOut, Bell, Globe, BookOpen } from "lucide-react";
import { Link } from "react-router-dom";
import PropTypes from "prop-types";
import { LANGUAGE_OPTIONS } from "../../translations/dealerPortal";
import { LOJACK_LOGO } from "../../config";

const DealerHeader = ({
  dealer, stats, dealerRole, lang, changeLang, handleLogout,
  notifications, unreadCount, showNotifications, setShowNotifications,
  fetchNotifications, markNotificationRead, markAllRead, notifRef, t, manualUpdate, onManualOpen
}) => (
  <div className="bg-[#111] border-b border-gray-800">
    <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
      <div className="flex items-center gap-3">
        <img src={LOJACK_LOGO} alt="LoJack Tracker" className="h-7" data-testid="dealer-header-logo" />
        <span className={`px-2 py-0.5 text-xs rounded-full ml-1 ${dealerRole === 'seller' ? 'bg-blue-500/20 text-blue-400' : 'bg-lojack-red/20 text-lojack-red'}`} data-testid="dealer-role-badge">
          {dealerRole === 'seller' ? t.seller : t.dealer}
        </span>
      </div>
      <div className="flex items-center gap-4">
        {(dealer || stats) && <span className="text-white text-sm font-medium hidden sm:block" data-testid="logged-user-name">{dealer?.first_name || stats?.first_name} {dealer?.last_name || stats?.last_name}</span>}
        <span className="text-gray-400 text-sm hidden sm:block">{stats?.company_name || ''} ({stats?.dealer_code})</span>
        <select value={lang} onChange={e => changeLang(e.target.value)}
          className="bg-transparent text-gray-500 text-xs border border-gray-800 rounded-lg px-2 py-1 focus:outline-none focus:border-lojack-red cursor-pointer hidden sm:block"
          data-testid="header-lang-selector">
          {LANGUAGE_OPTIONS.map(l => <option key={l.code} value={l.code} style={{backgroundColor:'#111'}}>{l.label}</option>)}
        </select>
        {dealerRole === 'dealer' && (
          <div className="relative" ref={notifRef}>
            <button onClick={() => { setShowNotifications(!showNotifications); if (!showNotifications) fetchNotifications(); }}
              className="relative text-gray-500 hover:text-white transition-colors p-1" data-testid="notifications-bell">
              <Bell size={18} />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-lojack-red text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center" data-testid="notifications-badge">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>
            {showNotifications && (
              <div className="absolute right-0 top-10 w-80 bg-[#1A1A1A] border border-gray-700 rounded-xl shadow-2xl z-50 overflow-hidden" data-testid="notifications-dropdown">
                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-700">
                  <h3 className="text-sm font-semibold text-white">{t.notifications}</h3>
                  {unreadCount > 0 && (
                    <button onClick={markAllRead} className="text-xs text-lojack-red hover:underline" data-testid="notifications-read-all">
                      {t.markAllRead}
                    </button>
                  )}
                </div>
                <div className="max-h-72 overflow-y-auto">
                  {notifications.length === 0 ? (
                    <div className="px-4 py-6 text-center text-gray-500 text-sm">{t.noNotifications}</div>
                  ) : (
                    notifications.map(n => (
                      <div key={n.id}
                        onClick={() => { if (!n.read) markNotificationRead(n.id); }}
                        className={`px-4 py-3 border-b border-gray-800 cursor-pointer transition-colors hover:bg-gray-800/50 ${!n.read ? 'bg-lojack-red/5 border-l-2 border-l-lojack-red' : ''}`}
                        data-testid={`notification-item-${n.id}`}>
                        <p className={`text-sm ${!n.read ? 'text-white font-medium' : 'text-gray-400'}`}>{n.title}</p>
                        <p className="text-xs text-gray-500 mt-0.5">{n.message}</p>
                        <p className="text-[10px] text-gray-600 mt-1">{new Date(n.created_at).toLocaleString('it-IT', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</p>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
        )}
        <Link
          to={dealerRole === 'seller' ? '/seller/manual' : '/dealer/manual'}
          className="relative text-gray-500 hover:text-white transition-colors"
          title={t.manual || 'Manuale Operativo'}
          aria-label={t.manual || 'Manuale Operativo'}
          data-testid="dealer-header-manual-link"
          onClick={onManualOpen}
        >
          <BookOpen size={18} />
          {manualUpdate && <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-lojack-red" data-testid="dealer-header-manual-dot" />}
        </Link>
        <button onClick={handleLogout} className="text-gray-500 hover:text-white transition-colors" data-testid="dealer-logout"><LogOut size={18} /></button>
      </div>
    </div>
  </div>
);

export default DealerHeader;

DealerHeader.propTypes = {
  dealer: PropTypes.object,                    // may be null right after login while /me resolves
  stats: PropTypes.object,                     // null while stats endpoint loads
  dealerRole: PropTypes.string,
  lang: PropTypes.string.isRequired,
  changeLang: PropTypes.func.isRequired,
  handleLogout: PropTypes.func.isRequired,
  notifications: PropTypes.array,              // defaults to [] when empty
  unreadCount: PropTypes.number,
  showNotifications: PropTypes.bool,
  setShowNotifications: PropTypes.func,
  fetchNotifications: PropTypes.func,
  markNotificationRead: PropTypes.func,
  markAllRead: PropTypes.func,
  notifRef: PropTypes.object,                  // useRef object — outside-click detection
  t: PropTypes.object.isRequired,
};
